import React, { useEffect, useState } from 'react';
import { Link as RouterLink, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Container,
  Paper,
  Snackbar,
  Alert,
  Typography,
  useMediaQuery,
  useTheme,
  Badge,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import AppHeader from '../AppHeader';
import { useAuth } from '../../contexts/AuthContext';
import { getTeacherMe } from '../../services/teacher';
import { useTeacherMessaging } from '../../contexts/TeacherMessagingContext';

function pathMatch(pathname, to) {
  if (to === '/teacher/inbox') {
    return pathname.startsWith('/teacher/inbox');
  }
  return pathname === to || pathname.startsWith(`${to}/`);
}

function TeacherNavTabs() {
  const { unreadCount } = useTeacherMessaging();
  const location = useLocation();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));

  const links = [
    { to: '/teacher/attendance', label: 'Attendance', icon: <EventAvailableIcon />, badge: 0 },
    { to: '/teacher/emergency-info', label: 'Emergency Info', icon: <MedicalInformationIcon />, badge: 0 },
    { to: '/teacher/newsletters', label: 'Newsletters', icon: <NewspaperIcon />, badge: 0 },
    { to: '/teacher/inbox', label: 'Inbox', icon: <MailOutlineIcon />, badge: unreadCount },
  ];

  if (narrow) {
    const idx = Math.max(
      0,
      links.findIndex((l) => pathMatch(location.pathname, l.to))
    );
    return (
      <Paper elevation={3} sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100 }}>
        <BottomNavigation value={idx} showLabels>
          {links.map((l, i) => (
            <BottomNavigationAction
              key={l.to}
              label={l.label}
              icon={
                l.badge > 0 ? (
                  <Badge color="error" badgeContent={l.badge}>
                    {l.icon}
                  </Badge>
                ) : (
                  l.icon
                )
              }
              component={RouterLink}
              to={l.to}
              value={i}
            />
          ))}
        </BottomNavigation>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        px: 2,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        position: 'sticky',
        top: 0,
        zIndex: 9,
      }}
    >
      {links.map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          style={({ isActive }) => ({
            textDecoration: 'none',
            padding: '8px 12px',
            borderRadius: 8,
            fontWeight: isActive ? 700 : 500,
            color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
            backgroundColor: isActive ? theme.palette.action.hover : 'transparent',
          })}
        >
          {l.label}
          {l.badge > 0 ? (
            <Box component="span" sx={{ ml: 0.5, color: 'error.main', fontWeight: 700 }}>
              ({l.badge})
            </Box>
          ) : null}
        </NavLink>
      ))}
    </Box>
  );
}

export default function TeacherLayout() {
  const { user, logout } = useAuth();
  const { toast, closeToast } = useTeacherMessaging();
  const [me, setMe] = useState(null);
  const [meError, setMeError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getTeacherMe()
      .then((res) => {
        if (!cancelled) setMe(res.data);
      })
      .catch((e) => {
        if (!cancelled) setMeError(e.response?.data?.detail || e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const classroom = me?.assigned_classroom;
  const subtitle = classroom
    ? `${classroom.label || classroom.class_num || 'Class'}${classroom.grade ? ` — ${classroom.grade}` : ''}${
        classroom.session_name ? ` · ${classroom.session_name}` : ''
      }`
    : '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', pb: { xs: 8, md: 0 } }}>
      <AppHeader
        rightContent={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.email}
            </Typography>
            <Typography
              variant="body2"
              component="button"
              type="button"
              onClick={logout}
              sx={{
                color: '#fff',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Logout
            </Typography>
          </Box>
        }
      />

      <Container maxWidth="md" sx={{ flex: 1, display: 'flex', flexDirection: 'column', px: { xs: 1, sm: 2 } }}>
        <Box sx={{ py: 2 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            My classroom
          </Typography>
          {subtitle && (
            <Typography variant="subtitle1" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {meError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {meError}
            </Alert>
          )}
        </Box>

        {!classroom && me && !meError ? (
          <Alert severity="info" sx={{ my: 2 }}>
            You&apos;re not assigned to a classroom yet. Contact your administrator.
          </Alert>
        ) : (
          <>
            <TeacherNavTabs />
            <Box sx={{ flex: 1, py: 2 }}>
              <Outlet />
            </Box>
          </>
        )}
      </Container>

      <Snackbar open={toast.open} autoHideDuration={5000} onClose={closeToast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={closeToast} severity="info" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
